import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Zap, Database, BarChart3, CheckCircle, AlertCircle, Info } from 'lucide-react';

const StreamingContainer = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
  justify-content: flex-start;
  direction: ltr;  /* Force LTR for layout - bot on left */
`;

const BotAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4FB8B8 0%, #1B8C3C 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;

  svg {
    width: 20px;
    height: 20px;
    color: white;
  }

  &::after {
    content: '';
    position: absolute;
    top: -2px;
    right: -2px;
    width: 12px;
    height: 12px;
    background: #22c55e;
    border: 2px solid white;
    border-radius: 50%;
    animation: pulse 2s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.2); opacity: 0.7; }
  }
`;

const StreamingBubble = styled.div`
  background: white;
  padding: 20px;
  border-radius: 20px 20px 4px 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  max-width: 85%;
  min-width: 300px;
  direction: rtl;
`;

const StreamingHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-weight: 600;
  color: #2d3748;
  font-size: 14px;
  direction: rtl;
`;

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StepItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background: ${props => {
    if (props.status === 'error') return '#fef2f2';
    if (props.status === 'warning') return '#fffbeb';
    if (props.status === 'success') return '#f0fdf4';
    return '#f8fafc';
  }};
  border-right: 3px solid ${props => {
    if (props.status === 'error') return '#ef4444';
    if (props.status === 'warning') return '#f59e0b';
    if (props.status === 'success') return '#22c55e';
    return '#1B8C3C';
  }};
  direction: rtl;
`;

const StepIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => {
    if (props.status === 'error') return '#ef4444';
    if (props.status === 'warning') return '#f59e0b';
    if (props.status === 'success') return '#22c55e';
    return '#1B8C3C';
  }};
  color: white;
  flex-shrink: 0;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const StepContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  direction: rtl;
  text-align: right;
`;

const StepMessage = styled.div`
  font-size: 13px;
  color: #4a5568;
  line-height: 1.4;
`;

const StepTimestamp = styled.div`
  font-size: 11px;
  color: #718096;
`;

const CurrentProcessing = styled(motion.div)`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: linear-gradient(135deg, #1B8C3C 0%, #006200 100%);
  color: white;
  border-radius: 8px;
  margin-top: 8px;
  direction: rtl;
`;

const LoadingDots = styled.div`
  display: flex;
  gap: 4px;

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: white;
    animation: loadingDot 1.5s ease-in-out infinite;
  }

  .dot:nth-child(2) {
    animation-delay: 0.2s;
  }

  .dot:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes loadingDot {
    0%, 60%, 100% {
      transform: translateY(0);
      opacity: 0.7;
    }
    30% {
      transform: translateY(-10px);
      opacity: 1;
    }
  }
`;

// Map raw pipeline step names → friendly, outcome-framed Arabic. Telemetry like "parse_question"
// must NEVER reach the user; anything unrecognized falls back to a neutral processing phrase.
const STEP_LABELS = [
  [/parse|understand/i, 'أفهم سؤالك'],
  [/translat/i, 'أُحضّر السؤال'],
  [/rephrase|reformulat/i, 'أُحسّن صياغة السؤال'],
  [/keyword|index|lsh|retriev|schema|context|column|table.?select/i, 'أراجع بنية البيانات'],
  [/candidate|generat|write|compose|sql/i, 'أكتب الاستعلام'],
  [/execut|run|query/i, 'أنفّذ الاستعلام على البيانات'],
  [/revis|fix|correct|repair/i, 'أراجع وأصحّح'],
  [/chart|visual|plot/i, 'أجهّز الرسم البياني'],
  [/synthes|answer|final|summar/i, 'أصيغ الإجابة'],
];
const friendlyStep = (raw) => {
  const s = String(raw || '').trim();
  if (/[؀-ۿ]/.test(s)) return s;           // already Arabic/friendly → keep
  for (const [re, label] of STEP_LABELS) if (re.test(s)) return label;
  return 'أعالج طلبك';                                 // never leak raw telemetry
};

const getStepIcon = (step, status) => {
  if (status === 'error') return <AlertCircle />;
  if (status === 'warning') return <AlertCircle />;
  if (status === 'success') return <CheckCircle />;
  if (status === 'info') return <Info />;
  
  // Determine icon based on step content
  if (step.includes('SQL') || step.includes('🔍') || step.includes('database')) {
    return <Database />;
  }
  if (step.includes('Chart') || step.includes('📊') || step.includes('visualization')) {
    return <BarChart3 />;
  }
  return <Zap />;
};

function StreamingIndicator({ currentSteps, processingStep, isProcessing }) {
  // Hide completely when not processing, regardless of steps
  if (!isProcessing) {
    return null;
  }
  
  // Also hide if no steps and no processing step
  if (!currentSteps || (currentSteps.length === 0 && !processingStep)) {
    return null;
  }

  // Find the current processing step (the one that's not completed)
  const activeStep = currentSteps?.find(step => step.status === 'processing');

  return (
    <StreamingContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <BotAvatar>
        <Bot />
      </BotAvatar>
      <StreamingBubble>
        <StreamingHeader>
          <Zap size={16} />
          جاري معالجة طلبك...
        </StreamingHeader>
        
        <StepsContainer>
          <AnimatePresence>
            {currentSteps && currentSteps.map((step) => (
              <StepItem
                key={step.id}
                status={step.status}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
              >
                <StepIcon status={step.status}>
                  {getStepIcon(step.step, step.status)}
                </StepIcon>
                <StepContent>
                  <StepMessage>{friendlyStep(step.step)}</StepMessage>
                  <StepTimestamp>{step.timestamp}</StepTimestamp>
                </StepContent>
              </StepItem>
            ))}
          </AnimatePresence>
        </StepsContainer>

        {/* Always show current processing step with loading animation - either from activeStep or processingStep */}
        {(isProcessing && (activeStep || processingStep)) && (
          <CurrentProcessing
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <LoadingDots>
              <div className="dot" />
              <div className="dot" />
              <div className="dot" />
            </LoadingDots>
            <div>الخطوة التالية...</div>
          </CurrentProcessing>
        )}
      </StreamingBubble>
    </StreamingContainer>
  );
}

export default StreamingIndicator;